import * as React from 'react';
import classnames from 'classnames';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { formStyles } from 'styles/formStyles';
import { Timestamps } from 'formComponents/Timestamps';
import { useSelector } from 'react-redux';
import {
    artikelGruppenByNrSelector,
    einheitenByCodeSelector,
    lagerByIntNrSelector,
    produktgruppenByNrSelector,
    produzentenByNrSelector,
} from 'store/lookups/selector';
import { formatDecimal, formatSmDate } from 'utils/formatter';
import nl2br from 'react-nl2br';
import { ArtikelEigenschaftLink } from 'api/artikelEigenschaftLink';
import { getApiUrl } from 'api/useApi';
import { BhCodeLabel } from 'utils/labels';
import { ArtikelDetail } from 'api/artikelDetail';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ArtikelDetailKalkulationen } from './ArtikelDetailKalkulationen';
import { ArtikelMaschinentype } from '../../../api/artikelMaschinentype';

const theme: ITheme = getTheme();
const { palette } = theme;

export const styles = mergeStyleSets({
    actions: {
        display: 'grid',
        gridColumnGap: 10,
        gridTemplateColumns: 'max-content max-content',
    },
    showCalculationButton: {
        color: palette.white,
        background: palette.yellow,
        borderColor: palette.yellow,
        selectors: {
            '&:disabled': {
                color: palette.neutralTertiary,
                borderColor: palette.neutralLighter,
                backgroundColor: palette.neutralLighter,
            },
            '&:hover': {
                color: palette.white,
                background: palette.yellowDark,
            },
        },
    },
});

interface ArtikelDetailItemProps {
    artikelDetail: ArtikelDetail;
    artikelEigenschaftLinkList: ArtikelEigenschaftLink[];
    artikelMachinentypeList: ArtikelMaschinentype[];
    viewMode: 'short' | 'long';
    editArtikel?: (artikelDetail: ArtikelDetail) => void;
}

export const ArtikelDetailItem: React.FC<ArtikelDetailItemProps> = ({
    artikelDetail,
    artikelEigenschaftLinkList,
    viewMode,
    editArtikel,
}) => {
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const produktgruppenByNr = useSelector(produktgruppenByNrSelector);
    const produzentenByNr = useSelector(produzentenByNrSelector);
    const lagerByIntNr = useSelector(lagerByIntNrSelector);
    const [showCalculation, setShowCalculation] = React.useState(false);

    const { artikel } = artikelDetail;

    return (
        <div
            className={classnames(formStyles.listItem, {
                [formStyles.archived]: artikelDetail.archiv,
            })}
        >
            <div className={formStyles.topBlock}>
                <div>
                    <div className={formStyles.listTitle}>
                        {artikelDetail.artikelNr + ' - ' + (artikelDetail.artikel?.text || '?')}
                        {' (' +
                            (artikelGruppenByNr[artikelDetail.artikelGruppeNr]?.text || artikelDetail.artikelGruppeNr) +
                            ')'}
                    </div>
                </div>
                <div />
                <div className={styles.actions}>
                    <DefaultButton
                        onClick={(event) => {
                            event?.preventDefault();
                            event?.stopPropagation();
                            setShowCalculation(!showCalculation);
                        }}
                        text="Kalkulation anzeigen"
                    />

                    {editArtikel && (
                        <PrimaryButton
                            onClick={() => {
                                editArtikel(artikelDetail);
                            }}
                            text="Bearbeiten"
                        />
                    )}
                </div>
            </div>
            {viewMode === 'short' ? (
                <div className={formStyles.eigthColumnsBlock}>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>Mindestbest:</div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            {formatDecimal(artikelDetail.minBestand, 4)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>Offene Best:</div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            {formatDecimal(artikelDetail.offenBest, 4)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>Offene Best (D):</div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            {formatDecimal(artikelDetail.offenBestD, 4)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>Offene Lief:</div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            {formatDecimal(artikelDetail.offenLief, 4)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>Offene Lief (D):</div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            {formatDecimal(artikelDetail.offenLiefD, 4)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>Reserviert:</div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            {formatDecimal(artikelDetail.offenRes, 4)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>
                            <strong>Bestand:</strong>
                        </div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            <strong>{formatDecimal(artikelDetail.bestand, 4)}</strong>
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey100}>
                            <strong>Verfügbar:</strong>
                        </div>
                        <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                            <strong>{formatDecimal(artikelDetail.bestand - artikelDetail.offenRes, 4)}</strong>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        {artikelDetail?.archiv && (
                            <div className={formStyles.line}>
                                <strong>Archiviert!</strong>
                            </div>
                        )}
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Artikelgruppe:</div>
                            <div className={formStyles.dataValue}>
                                {artikelGruppenByNr[artikelDetail.artikelGruppeNr]?.text ||
                                    artikelDetail.artikelGruppeNr}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Artikel-Nr.:</div>
                            <div className={formStyles.dataValue}>{artikelDetail.artikelNr}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Artikel-Nr. - Lager:</div>
                            <div className={formStyles.dataValue}>{artikelDetail.lagerArtikelNr}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Art.-Nr. - Lieferant:</div>
                            <div className={formStyles.dataValue}>
                                {artikelDetail.hauptlieferant?.lieferantArtikelNr || ''}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Artikel-Nr - Kunde.:</div>
                            <div className={formStyles.dataValue}>{artikelDetail.nrKunde}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Produktgruppe:</div>
                            <div className={formStyles.dataValue}>
                                {artikelDetail.produktGruppeNr
                                    ? produktgruppenByNr[artikelDetail.produktGruppeNr]?.text ||
                                      artikelDetail.produktGruppeNr
                                    : ''}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Produzent:</div>
                            <div className={formStyles.dataValue}>
                                {artikelDetail.produzentNr
                                    ? produzentenByNr[artikelDetail.produzentNr]?.name || artikelDetail.produzentNr
                                    : ''}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Einheit:</div>
                            <div className={formStyles.dataValue}>
                                {artikel?.einheit ? einheitenByCode[artikel?.einheit].text || artikel?.einheit : '?'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Packeinheit:</div>
                            <div className={formStyles.dataValue}>{formatDecimal(artikel?.packEinheit || 0, 2)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Einh. je Pal/Kart:</div>
                            <div className={formStyles.dataValue}>{formatDecimal(artikel?.einhProPal || 0, 2)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Bestelleinheit:</div>
                            <div className={formStyles.dataValue}>
                                {(artikel?.bestPal ? 'Palette' : '') +
                                    (artikel?.bestPal && artikel?.bestKart ? ', ' : '') +
                                    (artikel?.bestKart ? 'Karton' : '') +
                                    (!artikel?.bestPal && !artikel?.bestKart ? '-' : '')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>BH-Code:</div>
                            <div className={formStyles.dataValue}>
                                {artikel?.bhCode ? BhCodeLabel[artikel?.bhCode] || artikel?.bhCode : ''}{' '}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zolltarif:</div>
                            <div className={formStyles.dataValue}>{artikel?.zollTarif}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>EAN:</div>
                            <div className={formStyles.dataValue}>{artikel?.ean}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Kalk. Blatt:</div>
                            <div className={formStyles.dataValue}>{artikel?.kalkBlatt}</div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Stärke:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.staerke || 0, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>KernID (mm):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {artikel?.kernId}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Breite (m):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.breite || 0, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Halbschlauch:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {artikel?.halbeBreite ? 'Ja' : 'Nein'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Länge (m):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.laufmeter || 0, 2)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Fläche (m²):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.quadrmeter || 0, 2)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Spez. Gewicht:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.spezGew || 0, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Kerngewicht (kg):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.kernGew || 0, 3)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Netto-Gew. (kg):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.nettoGew || 0, 2)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Brutto-Gew. (kg):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikel?.rollenGew || 0, 2)}
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.line}>Maschinentype:</div>
                        <div className={formStyles.line}>{artikel?.prodInfoUes}</div>
                        <div className={formStyles.line}>Produkt-Info (Überschrift):</div>
                        <div className={formStyles.line}>{artikel?.prodInfoUes}</div>
                        <div className={formStyles.line}>Produkt-Info:</div>
                        <div className={formStyles.line}>{nl2br(artikel?.prodInfo)}</div>
                        <div className={formStyles.line}>Bemerkung:</div>
                        <div className={formStyles.line}>{nl2br(artikel?.bemerkung)}</div>
                        <div className={formStyles.line}>Beschreibung (Datei):</div>
                        <div className={formStyles.line}>
                            {artikel?.doku && (
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={getApiUrl('attachment/?fileName=' + encodeURIComponent(artikel?.doku))}
                                >
                                    {artikel?.doku}
                                </a>
                            )}
                        </div>
                        {artikelEigenschaftLinkList.map((artikelEigenschaftLink) => (
                            <div className={formStyles.line}>{artikelEigenschaftLink?.artikelEigenschaft?.text}</div>
                        ))}
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lager:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {artikelDetail.lagerIntNr ? lagerByIntNr[artikelDetail.lagerIntNr]?.name : ''}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Regal / Fach:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {artikelDetail.regal + '/' + artikelDetail.fach}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Verbr. / Monat:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {artikelDetail.verbrD}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Kalk.-Dat:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatSmDate(artikelDetail.verbrKalkDat)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lief. Zeit (Tg):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {artikelDetail.bestZeit}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Mindestbestand:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.minBestand, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Min.Best.-Menge:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.minBestMenge, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Offene Best:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.offenBest, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Offene Best (D):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.offenBestD, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Offene Lief:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.offenLief, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Offene Lief (D):</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.offenLiefD, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Reserviert:</div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                {formatDecimal(artikelDetail.offenRes, 4)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>
                                <strong>Bestand:</strong>
                            </div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                <strong>{formatDecimal(artikelDetail.bestand, 4)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>
                                <strong>Verfügbar:</strong>
                            </div>
                            <div className={classnames(formStyles.dataValue, formStyles.dataValueRight)}>
                                <strong>{formatDecimal(artikelDetail.bestand - artikelDetail.offenRes, 4)}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCalculation && (
                <ArtikelDetailKalkulationen
                    artikelGruppeNr={artikelDetail.artikelGruppeNr}
                    artikelNr={artikelDetail.artikelNr}
                    embedded
                />
            )}

            <Timestamps
                createdDate={artikelDetail?.anlageDat}
                createdDecimalTime={artikelDetail?.anlageUhr}
                createdUser={artikelDetail?.anlageUser}
                modifiedDate={artikelDetail?.updDat}
                modifiedDecimalTime={artikelDetail?.updUhr}
                modifiedUser={artikelDetail?.updUser}
            />
        </div>
    );
};
